import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import injectReducer from 'utils/injectReducer';
import { makeSelectStepOtherInsuranceCoverageTugo, makeSelectStepOtherInsuranceCoverageTugoErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';
import { appConfig } from '../../../configureApp';
import { ErrorMessage, Validate, ValidateGroup } from '../../../components/Validation';
import { required, length } from '../../../utils/validation/validator';

import WizardNavigationButtons from '../components/WizardNavigationButtons';
import PageSectionHeader from '../components/PageSectionHeader';
import FlexBox from '../../../components/FlexBox';
import OptionsContainer from '../components/OptionsContainer';
import {
  handleFormDataChange,
  nextSlide,
  resetFormData,
  resetFormFields,
} from './actions';
import { saveFormData } from '../actions';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import { makeSelectFormHasChanges } from '../../App/selectors';
import { setFormHasChanges } from '../../App/actions';

export class StepOtherInsuranceCoverageTugo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
    };

    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.saveButtonId = `saveBtn_${Math.random()}`;
    this.nextButton = null;
  }

  onChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.value;
    this.props.onFormDataChange(`form.${propertyName}`, value);
    // When switching to "No" for provincial health, clear province
    if (propertyName === 'hasProvincialHealthCoverage' && value === 'false') {
      this.props.onFormDataChange('form.provincialHealthProvince', '');
    }
    // When switching to "No", clear the coverage types selection
    if (propertyName === 'hasOtherInsuranceCoverage' && value === 'false') {
      this.props.onResetFormFields(['otherInsuranceCoverageTypes']);
    }
    this.props.setFormHasChanges(true);
  };

  onCoverageTypeChange = (event) => {
    const { otherInsuranceCoverageTypes } = this.props.form;
    const currentList = !!otherInsuranceCoverageTypes && typeof otherInsuranceCoverageTypes.toJS === 'function'
      ? otherInsuranceCoverageTypes.toJS()
      : otherInsuranceCoverageTypes || [];
    const updated = [...currentList];
    const { name, checked } = event.target;
    if (checked) {
      if (!updated.includes(name)) updated.push(name);
    } else {
      const idx = updated.indexOf(name);
      if (idx !== -1) updated.splice(idx, 1);
      // Clear employer fields when Employer or group health plan is unchecked
      if (name === 'Employer or group health plan') {
        this.props.onFormDataChange('form.employerInsuranceCompany', '');
        this.props.onFormDataChange('form.employerPolicyGroupNo', '');
      }
      // Clear credit card fields when Credit card insurance coverage is unchecked
      if (name === 'Credit card insurance coverage') {
        this.props.onFormDataChange('form.creditCardProvider', '');
        this.props.onFormDataChange('form.creditCardFirst4Digits', '');
        this.props.onFormDataChange('form.creditCardLast4Digits', '');
        this.props.onFormDataChange('form.creditCardInsuranceProvider', '');
      }
      // Clear another travel fields when Travel insurance policy from another insurer is unchecked
      if (name === 'Travel insurance policy from another insurer') {
        this.props.onFormDataChange('form.anotherTravelInsuranceCompany', '');
        this.props.onFormDataChange('form.anotherTravelPolicyNumber', '');
      }
    }
    this.props.onFormDataChange('form.otherInsuranceCoverageTypes', updated);
    this.props.setFormHasChanges(true);
  };

  handleNext = () => {
    if (this.props.submitted) {
      if (this.props.next) this.props.next();
      return;
    }
    const isValid = this.validatorGroup.checkValidation();
    this.props.onNext(!isValid);
    if (!isValid) {
      this.setState((prevState) => ({ validationCounter: prevState.validationCounter + 1 }));
    } else {
      this.handleFormDataSave();
      if (this.props.next) this.props.next();
    }
  };

  onNumericChange = (propertyName) => (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 4);
    this.props.onFormDataChange(`form.${propertyName}`, value);
    this.props.setFormHasChanges(true);
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
    this.props.setFormHasChanges(false);
  };

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') {
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
    }
  };

  render() {
    const { isSummary, submitted, loading, intl } = this.props;
    const { hasProvincialHealthCoverage, provincialHealthProvince, hasOtherInsuranceCoverage, otherInsuranceCoverageTypes, employerInsuranceCompany, employerPolicyGroupNo, creditCardProvider, creditCardFirst4Digits, creditCardLast4Digits, creditCardInsuranceProvider, anotherTravelInsuranceCompany, anotherTravelPolicyNumber } = this.props.form;
    const isReadOnly = submitted || isSummary;
    const selectedTypes = !!otherInsuranceCoverageTypes && typeof otherInsuranceCoverageTypes.toJS === 'function'
      ? otherInsuranceCoverageTypes.toJS()
      : otherInsuranceCoverageTypes || [];
    const insuranceCompanyNameSelected = selectedTypes.includes('Employer or group health plan');
    const creditCardSelected = selectedTypes.includes('Credit card insurance coverage');
    const anotherTravelSelected = selectedTypes.includes('Travel insurance policy from another insurer');
    const canMoveNext = (hasProvincialHealthCoverage !== 'true' || !!provincialHealthProvince)
      && (hasOtherInsuranceCoverage !== 'true' || selectedTypes.length > 0)
      && (!insuranceCompanyNameSelected || !!employerInsuranceCompany)
      && (!creditCardSelected || (!!creditCardProvider && (creditCardFirst4Digits || '').length === 4 && (creditCardLast4Digits || '').length === 4))
      && (!anotherTravelSelected || !!anotherTravelInsuranceCompany);
    const downloadUrl = provincialHealthProvince && appConfig.ghipProvincialFormUrls ? appConfig.ghipProvincialFormUrls[provincialHealthProvince] : null;

    const CANADIAN_PROVINCES = [
      'Alberta',
      'British Columbia',
      'Manitoba',
      'New Brunswick',
      'Newfoundland and Labrador',
      'Northwest Territories',
      'Nova Scotia',
      'Nunavut',
      'Ontario',
      'Prince Edward Island',
      'Quebec',
      'Saskatchewan',
      'Yukon',
    ];

    const COVERAGE_TYPES = [
      { name: 'Employer or group health plan', message: messages.coverageTypeEmployerGroup },
      { name: 'Credit card insurance coverage', message: messages.coverageTypeCreditCard },
      { name: 'Travel insurance policy from another insurer', message: messages.coverageTypeAnotherTravel },
    ];

    return (
      <div className="app-content">
        <div className="mb-4">
          <h4>
            <FormattedMessage {...messages.heading} />
          </h4>
        </div>

        {/* Question 1: Provincial Health Coverage */}
        <div className="col-sm-12 form-group">
          <div className="mt-2">
            <PageSectionHeader>
              <FormattedMessage {...messages.hasProvincialHealthCoverage} />
            </PageSectionHeader>
            <FlexBox>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  value="true"
                  checked={hasProvincialHealthCoverage === 'true'}
                  onChange={this.onChange('hasProvincialHealthCoverage')}
                />
                <FormattedMessage {...messages.yes} />
              </OptionsContainer>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  value="false"
                  checked={hasProvincialHealthCoverage === 'false' || !hasProvincialHealthCoverage}
                  onChange={this.onChange('hasProvincialHealthCoverage')}
                />
                <FormattedMessage {...messages.no} />
              </OptionsContainer>
            </FlexBox>
          </div>
        </div>

        {hasProvincialHealthCoverage === 'true' && (
          <div className="col-sm-12 form-group">
            <div className="ml-4 mt-2 mb-3">
              <div className="form-group mt-2">
                <label htmlFor="provincialHealthProvince" className="col-form-label">
                  <FormattedMessage {...messages.provincialHealthProvinceLabel} /><span style={{ color: 'red' }}>{' *'}</span>
                </label>
                <Validate
                  name="provincialHealthProvince"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={provincialHealthProvince}
                >
                  <select
                    id="provincialHealthProvince"
                    className="form-control"
                    value={provincialHealthProvince || ''}
                    onChange={this.onChange('provincialHealthProvince')}
                    disabled={isReadOnly}
                  >
                    <option value="" />
                    {CANADIAN_PROVINCES.map((province) => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                  <ErrorMessage for="required">
                    <FormattedMessage {...messages.provincialHealthProvinceRequired} />
                  </ErrorMessage>
                </Validate>
              </div>
              {downloadUrl && (
                <div className="mt-2">
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <FormattedMessage {...messages.provincialHealthDownloadButton} />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Question 2: Other Insurance Coverage */}
        <div className="col-sm-12 form-group">
          <div className="mt-2">
            <PageSectionHeader>
              <FormattedMessage {...messages.hasOtherInsuranceCoverage} />
            </PageSectionHeader>
            <FlexBox>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  value="true"
                  checked={hasOtherInsuranceCoverage === 'true'}
                  onChange={this.onChange('hasOtherInsuranceCoverage')}
                />
                <FormattedMessage {...messages.yes} />
              </OptionsContainer>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  value="false"
                  checked={hasOtherInsuranceCoverage === 'false' || !hasOtherInsuranceCoverage}
                  onChange={this.onChange('hasOtherInsuranceCoverage')}
                />
                <FormattedMessage {...messages.no} />
              </OptionsContainer>
            </FlexBox>
          </div>
        </div>

        {hasOtherInsuranceCoverage === 'true' && (
          <div className="col-sm-12 form-group">
            <div className="mt-2">
              <PageSectionHeader>
                <FormattedMessage {...messages.whatTypeOfCoverage} />
              </PageSectionHeader>
              {this.state.validationCounter > 0 && selectedTypes.length === 0 && (
                <div style={{ color: 'red', marginTop: 4 }}>
                  <FormattedMessage {...messages.coverageTypeRequired} />
                </div>
              )}
              {COVERAGE_TYPES.map((type) => (
                <div key={type.name} className="col-sm-12 form-group">
                  <input
                    disabled={isReadOnly}
                    type="checkbox"
                    name={type.name}
                    checked={selectedTypes.includes(type.name)}
                    onChange={this.onCoverageTypeChange}
                  />
                  <span>
                    <FormattedMessage {...type.message} />
                  </span>
                  {type.name === 'Employer or group health plan' && insuranceCompanyNameSelected && (
                    <div className="ml-4 mt-2 mb-3">
                      <div className="form-group row align-items-center mt-2">
                        <label htmlFor="employerInsuranceCompany" className="col-sm-4 col-form-label">
                          <FormattedMessage {...messages.employerInsuranceCompanyLabel} /><span style={{ color: 'red' }}>{' *'}</span>
                        </label>
                        <div className="col-sm-8">
                          <Validate
                            name="employerInsuranceCompany"
                            validators={[required]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={employerInsuranceCompany}
                          >
                            <input
                              id="employerInsuranceCompany"
                              type="text"
                              className="form-control"
                              value={employerInsuranceCompany || ''}
                              onChange={this.onChange('employerInsuranceCompany')}
                              disabled={isReadOnly}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.employerInsuranceCompanyRequired} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                      </div>
                      <div className="form-group row align-items-center mt-2">
                        <label htmlFor="employerPolicyGroupNo" className="col-sm-4 col-form-label">
                          <FormattedMessage {...messages.employerPolicyGroupNoLabel} />
                        </label>
                        <div className="col-sm-8">
                          <input
                            id="employerPolicyGroupNo"
                            type="text"
                            className="form-control"
                            value={employerPolicyGroupNo || ''}
                            onChange={this.onChange('employerPolicyGroupNo')}
                            disabled={isReadOnly}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {type.name === 'Credit card insurance coverage' && creditCardSelected && (
                    <div className="ml-4 mt-2 mb-3">
                      <div className="form-group row align-items-center mt-2">
                        <label htmlFor="creditCardProvider" className="col-sm-4 col-form-label">
                          <FormattedMessage {...messages.creditCardProviderLabel} /><span style={{ color: 'red' }}>{' *'}</span>
                        </label>
                        <div className="col-sm-8">
                          <Validate
                            name="creditCardProvider"
                            validators={[required]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={creditCardProvider}
                          >
                            <input
                              id="creditCardProvider"
                              type="text"
                              className="form-control"
                              value={creditCardProvider || ''}
                              placeholder="Visa, Mastercard, American Express, Other"
                              onChange={this.onChange('creditCardProvider')}
                              disabled={isReadOnly}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.creditCardProviderRequired} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                      </div>
                      <div className="form-group row align-items-center mt-2">
                        <label htmlFor="creditCardFirst4Digits" className="col-sm-4 col-form-label">
                          <FormattedMessage {...messages.creditCardFirst4DigitsLabel} /><span style={{ color: 'red' }}>{' *'}</span>
                        </label>
                        <div className="col-sm-8">
                          <Validate
                            name="creditCardFirst4Digits"
                            validators={[required, length(4)]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={creditCardFirst4Digits}
                          >
                            <input
                              id="creditCardFirst4Digits"
                              type="text"
                              className="form-control"
                              value={creditCardFirst4Digits || ''}
                              maxLength={4}
                              onChange={this.onNumericChange('creditCardFirst4Digits')}
                              disabled={isReadOnly}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.creditCardFirst4DigitsRequired} />
                            </ErrorMessage>
                            <ErrorMessage for="mLength">
                              <FormattedMessage {...messages.creditCardFirst4DigitsInvalid} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                      </div>
                      <div className="form-group row align-items-center mt-2">
                        <label htmlFor="creditCardLast4Digits" className="col-sm-4 col-form-label">
                          <FormattedMessage {...messages.creditCardLast4DigitsLabel} /><span style={{ color: 'red' }}>{' *'}</span>
                        </label>
                        <div className="col-sm-8">
                          <Validate
                            name="creditCardLast4Digits"
                            validators={[required, length(4)]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={creditCardLast4Digits}
                          >
                            <input
                              id="creditCardLast4Digits"
                              type="text"
                              className="form-control"
                              value={creditCardLast4Digits || ''}
                              maxLength={4}
                              onChange={this.onNumericChange('creditCardLast4Digits')}
                              disabled={isReadOnly}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.creditCardLast4DigitsRequired} />
                            </ErrorMessage>
                            <ErrorMessage for="mLength">
                              <FormattedMessage {...messages.creditCardLast4DigitsInvalid} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                      </div>
                      <div className="form-group row align-items-center mt-2">
                        <label htmlFor="creditCardInsuranceProvider" className="col-sm-4 col-form-label">
                          <FormattedMessage {...messages.creditCardInsuranceProviderLabel} />
                        </label>
                        <div className="col-sm-8">
                          <input
                            id="creditCardInsuranceProvider"
                            type="text"
                            className="form-control"
                            value={creditCardInsuranceProvider || ''}
                            onChange={this.onChange('creditCardInsuranceProvider')}
                            disabled={isReadOnly}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {type.name === 'Travel insurance policy from another insurer' && anotherTravelSelected && (
                    <div className="ml-4 mt-2 mb-3">
                      <div className="form-group row align-items-center mt-2">
                        <label htmlFor="anotherTravelInsuranceCompany" className="col-sm-4 col-form-label">
                          <FormattedMessage {...messages.anotherTravelInsuranceCompanyLabel} /><span style={{ color: 'red' }}>{' *'}</span>
                        </label>
                        <div className="col-sm-8">
                          <Validate
                            name="anotherTravelInsuranceCompany"
                            validators={[required]}
                            group={this.validatorGroup}
                            addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                            validChangeInGroup={this.validatorGroup.validChangeInGroup}
                            validationCounter={this.state.validationCounter}
                            validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                            defaultValue={anotherTravelInsuranceCompany}
                          >
                            <input
                              id="anotherTravelInsuranceCompany"
                              type="text"
                              className="form-control"
                              value={anotherTravelInsuranceCompany || ''}
                              onChange={this.onChange('anotherTravelInsuranceCompany')}
                              disabled={isReadOnly}
                            />
                            <ErrorMessage for="required">
                              <FormattedMessage {...messages.anotherTravelInsuranceCompanyRequired} />
                            </ErrorMessage>
                          </Validate>
                        </div>
                      </div>
                      <div className="form-group row align-items-center mt-2">
                        <label htmlFor="anotherTravelPolicyNumber" className="col-sm-4 col-form-label">
                          <FormattedMessage {...messages.anotherTravelPolicyNumberLabel} />
                        </label>
                        <div className="col-sm-8">
                          <input
                            id="anotherTravelPolicyNumber"
                            type="text"
                            className="form-control"
                            value={anotherTravelPolicyNumber || ''}
                            onChange={this.onChange('anotherTravelPolicyNumber')}
                            disabled={isReadOnly}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!isSummary && (
          <WizardNavigationButtons
            onBack={this.props.prev}
            onNext={this.handleNext}
            onSave={this.handleFormDataSave}
            canMoveNext={canMoveNext}
            listLength={1}
            submitted={submitted}
            loading={loading}
            saveButtonId={this.saveButtonId}
            handleKeyDown={this.handleKeyDown}
            intl={intl}
            hasFormChanges={this.props.hasFormChanges}
            nextButtonRef={(ref) => {
              this.nextButton = ref;
            }}
          />
        )}
      </div>
    );
  }
}

StepOtherInsuranceCoverageTugo.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  onResetFormFields: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  loading: PropTypes.bool,
  currentStep: PropTypes.number,
  step: PropTypes.number,
  intl: intlShape.isRequired,
  hasFormChanges: PropTypes.bool,
  setFormHasChanges: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectStepOtherInsuranceCoverageTugo(),
  errors: makeSelectStepOtherInsuranceCoverageTugoErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
  hasFormChanges: makeSelectFormHasChanges(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) =>
    dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onReset: () => dispatch(resetFormData()),
  onResetFormFields: (fields) => dispatch(resetFormFields(fields)),
  setFormHasChanges: (hasFormChanges) => dispatch(setFormHasChanges(hasFormChanges)),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps
);
const withReducer = injectReducer({
  key: 'stepOtherInsuranceCoverageTugo',
  reducer,
});

export default compose(
  withReducer,
  withConnect,
  injectIntl,
)(StepOtherInsuranceCoverageTugo);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.StepOtherInsuranceCoverage/index.js